import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogTitle } from '@headlessui/react';

import { queryClient } from 'services/http/queryClient';
import { logError } from 'services/logging/logging';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useForceProcessingMessage } from 'services/repositories/transactions/transactions';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { EMPTY_FUNCTION } from 'support/helpers/const/const';
import { MessageV2DTO } from 'support/types';
import {
  determineDuplicateAndChangeDocumentState,
  shouldDetermineTheDuplicateAndChangeDocumentState,
} from 'pages/Transactions/TransactionViewPage/DuplicateAndChangeDocumentBanner/helpers';
import { Alert } from 'components/Display/Alert/Alert';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Translate } from 'components/Translate/Translate';

import { ActionConfig } from '../components/types';

export const useForceDocumentProcessingAction = (message: MessageV2DTO): ActionConfig => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: forceProcessing, isLoading } = useForceProcessingMessage();

  const handleForceProcessing = () => {
    forceProcessing(
      { message: message.id },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          queryClient.invalidateQueries(useMessageWithProcessSpecification.getKey({ messageId: message.id }));
          addNotification(
            {
              title: t('transactions:show.actionButtons.forceDocumentProcessingModal.successNotification'),
            },
            NotificationType.success,
          );
        },
        onError: (e) => {
          logError(e);
          addNotification(
            {
              title: t('transactions:show.actionButtons.forceDocumentProcessingModal.errorNotification'),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  /**
   * i18n
   * t('transactions:show.actionButtons.forceDocumentProcessing')
   */
  return {
    label: 'transactions:show.actionButtons.forceDocumentProcessing',
    onClick: () => setIsModalOpen(true),
    analyticsId: 'transactions:force_document_processing',
    test: () =>
      shouldDetermineTheDuplicateAndChangeDocumentState(message) &&
      determineDuplicateAndChangeDocumentState(message) === 'duplicate',
    renderModal: () => (
      <ForceDocumentProcessingModal
        isLoading={isLoading}
        open={isModalOpen}
        setOpen={setIsModalOpen}
        onConfirm={handleForceProcessing}
      />
    ),
  };
};

type ForceDocumentProcessingModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
};

const ForceDocumentProcessingModal = ({ open, setOpen, onConfirm, isLoading }: ForceDocumentProcessingModalProps) => {
  const { t } = useTranslation();

  return (
    <BasePopup open={open} setOpen={isLoading ? EMPTY_FUNCTION : setOpen}>
      <BasePopup.Body>
        <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
          {t('transactions:show.actionButtons.forceDocumentProcessingModal.title')}
        </DialogTitle>
        <div className="mt-4">
          <Alert
            severity="warning"
            withoutIcon
            title={t('transactions:show.actionButtons.forceDocumentProcessingModal.alertTitle')}
          >
            {t('transactions:show.actionButtons.forceDocumentProcessingModal.description')}
          </Alert>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button
            analyticsId="force_document_processing_cancel"
            variant="secondary"
            disabled={isLoading}
            onClick={() => setOpen(false)}
            type="button"
          >
            <Translate i18nKey="common:cancel" />
          </Button>
          <Button
            analyticsId="force_document_processing_confirm"
            variant="primary"
            onClick={onConfirm}
            loading={isLoading}
          >
            <Translate i18nKey="transactions:show.actionButtons.forceDocumentProcessingModal.confirm" />
          </Button>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
};
